// Thumbnails from the sprite sheets in the project's CDN folder, each indexed by a WebVTT file whose cues' text is
// "<sheet>.jpg#xywh=x,y,w,h":
// - scene_thumbnails/scenes.vtt: one cue per scene, all on scenes.jpg
// - frame_sprites/thumbnails.vtt: one cue per second of video, on sheet_000.jpg, sheet_001.jpg... (padded to full size)
// Videos processed before the sprites existed fall back to frames from the API or the video itself.
import plainAxios from "axios";
import { ensureCdnCookie } from "../../../app/cdnCookie";
import { frameThumb } from "./frameThumbs";
import { thumbMs } from "./sceneModel";

const sprites = new Map(); // vtt url -> Promise<cues | null>

// "hh:mm:ss.ttt" or "mm:ss.ttt"
const sec = (t) => t.split(":").reduce((n, v) => n * 60 + parseFloat(v), 0);

/** cues [{s, e, src, tile?: [x, y, w, h]}] of a WebVTT thumbnail track; image paths are resolved against the vtt url */
export function parseThumbVtt(text, url) {
  const lines = String(text || "").split(/\r?\n/);
  const cues = [];
  lines.forEach((l, k) => {
    const t = l.match(/^\s*([\d:.]+)\s+-->\s+([\d:.]+)/);
    const ref = t && (lines[k + 1] || "").trim();
    if (!ref) return;
    const [path, frag = ""] = ref.split("#");
    const xywh = frag.match(/^xywh=(?:pixel:)?(\d+),(\d+),(\d+),(\d+)$/);
    cues.push({ s: sec(t[1]), e: sec(t[2]), src: new URL(path, url).href, tile: xywh ? xywh.slice(1).map(Number) : null });
  });
  return cues;
}

/**
 * style of an <img> of the whole sheet that shows the tile in a box of the tile's shape
 * @param sheet [width, height] of the sheet image as loaded (a sheet can be larger than its tiles reach)
 */
export function tileStyle(tile, sheet) {
  const [x, y, w, h] = tile;
  const pc = (v) => (v * 100).toFixed(3) + "%";
  return { left: pc(-x / w), top: pc(-y / h), width: pc(sheet[0] / w), height: pc(sheet[1] / h) };
}

function loadVtt(url, retry) {
  return plainAxios
    .get(url, { responseType: "text", withCredentials: true })
    .then((response) => {
      const cues = parseThumbVtt(response.data, url);
      return cues.length ? cues : null;
    })
    .catch((error) => {
      // no response: the CDN refused the cookie (its 403 has no CORS headers), so re-sign it once;
      // a 403 the browser can read is a file that isn't there
      if (!error.response && retry) return ensureCdnCookie(true).then(() => loadVtt(url, false));
      return null;
    });
}

// the cues of the vtt at url, loaded once, or null when there is none
function spriteCues(url) {
  if (!sprites.has(url)) sprites.set(url, ensureCdnCookie().then(() => loadVtt(url, true)));
  return sprites.get(url);
}

/** the project's scene sprite cues, or null when it has none */
export function sceneSprites(folder) {
  return spriteCues(folder + "/scene_thumbnails/scenes.vtt");
}

/**
 * thumbnail of scene x: {src, tile} (a sprite tile, see TileImg), {src} (a whole image), or null
 * @param folder the project's CDN folder url
 */
export function sceneThumb(folder, projectId, x) {
  return sceneSprites(folder).then((cues) => {
    const mid = (x.s + x.e) / 2;
    const cue = cues && cues.find((c) => mid >= c.s && mid < c.e);
    if (cue) return cue;
    return frameThumb(projectId, thumbMs(x)).then((src) => src && { src });
  });
}

/**
 * the frame sprite {src, tile} (see TileImg) covering time (s), or null when the project has no frame sprites
 * @param folder the project's CDN folder url
 */
export function frameSprite(folder, time) {
  return spriteCues(folder + "/frame_sprites/thumbnails.vtt").then((cues) => (cues && cues.find((c) => c.tile && time >= c.s && time < c.e)) || null);
}

/**
 * time (s) of the frame sprite showing the moment start..end: the first frame sampled (once a second) at or after its
 * start, so a moment starting just after a cut doesn't show the shot before it
 */
export const momentFrameTime = (start, end) => (Math.ceil(start) <= end ? Math.ceil(start) : start);
